// ============================================================================
// Document version control — content hashing (versioning brief A §3, layer 2)
// ============================================================================
// A signed document must never change. Three layers guard that:
//   1. the signature points at a specific VERSION ROW (version_document_id /
//      hosted_form_submissions.document_id — FKs, ON DELETE RESTRICT)
//   2. a CONTENT HASH taken at publish and snapshotted onto the signature —
//      if the version row is ever mutated, the hashes stop matching and we can
//      SAY SO rather than silently rendering different content (this file)
//   3. the rendered artefact stored at submission (document_html_url)
//
// Layer 2 only works if the hash is reproducible years later, so:
//   - the JSON is CANONICALISED (keys sorted) before hashing — an equal schema
//     must hash equal regardless of key order from the DB/driver
//   - SHA-256 is implemented HERE rather than via crypto.subtle. Web Crypto is
//     async, absent in the jest/jsdom environment, and unavailable outside a
//     secure context. Evidence code should be testable and synchronous; the
//     schemas are kilobytes, so the cost is irrelevant.
// ============================================================================

// --- canonical JSON ---------------------------------------------------------

// Recursively sort object keys; arrays keep their order (order is meaningful —
// blocks in a document are a sequence). `undefined` values are dropped, which
// is what JSON.stringify does anyway; doing it here keeps the two consistent.
function canonicalise(value) {
  if (Array.isArray(value)) return value.map(canonicalise);
  if (value && typeof value === 'object') {
    const out = {};
    for (const key of Object.keys(value).sort()) {
      if (value[key] !== undefined) out[key] = canonicalise(value[key]);
    }
    return out;
  }
  return value;
}

export function canonicalJsonString(value) {
  return JSON.stringify(canonicalise(value === undefined ? null : value));
}

// --- SHA-256 ----------------------------------------------------------------

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const rotr = (x, n) => ((x >>> n) | (x << (32 - n))) >>> 0;

// UTF-8 encode by hand rather than via TextEncoder: the hash must come out
// identical in the browser, in node, and in the jest/jsdom environment (which
// has no TextEncoder at this CRA version). Evidence code shouldn't depend on
// which runtime it happens to be in.
function utf8Bytes(str) {
  const out = [];
  for (let i = 0; i < str.length; i++) {
    let cp = str.codePointAt(i);
    if (cp > 0xffff) i++;                       // surrogate pair — consume both units
    else if (cp >= 0xd800 && cp <= 0xdfff) cp = 0xfffd; // lone surrogate → U+FFFD, as TextEncoder does
    if (cp < 0x80) {
      out.push(cp);
    } else if (cp < 0x800) {
      out.push(0xc0 | (cp >> 6), 0x80 | (cp & 63));
    } else if (cp < 0x10000) {
      out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63));
    } else {
      out.push(0xf0 | (cp >> 18), 0x80 | ((cp >> 12) & 63), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63));
    }
  }
  return new Uint8Array(out);
}

function toBytes(input) {
  if (input instanceof Uint8Array) return input;
  if (input instanceof ArrayBuffer) return new Uint8Array(input);
  if (ArrayBuffer.isView(input)) return new Uint8Array(input.buffer, input.byteOffset, input.byteLength);
  return utf8Bytes(String(input));
}

// SHA-256 (FIPS 180-4). Returns lowercase hex.
export function sha256Hex(input) {
  const bytes = toBytes(input);
  const len = bytes.length;
  // message + 0x80 + zero padding + 8-byte length, rounded up to a 64-byte block
  const padded = new Uint8Array((((len + 9) + 63) >> 6) << 6);
  padded.set(bytes);
  padded[len] = 0x80;

  const view = new DataView(padded.buffer);
  const bitLen = len * 8;
  view.setUint32(padded.length - 8, Math.floor(bitLen / 4294967296));
  view.setUint32(padded.length - 4, bitLen >>> 0);

  let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a;
  let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19;

  const w = new Uint32Array(64);
  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(offset + i * 4);
    for (let i = 16; i < 64; i++) {
      const a15 = w[i - 15], a2 = w[i - 2];
      const s0 = (rotr(a15, 7) ^ rotr(a15, 18) ^ (a15 >>> 3)) >>> 0;
      const s1 = (rotr(a2, 17) ^ rotr(a2, 19) ^ (a2 >>> 10)) >>> 0;
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }

    let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7;
    for (let i = 0; i < 64; i++) {
      const S1 = (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) >>> 0;
      const ch = ((e & f) ^ (~e & g)) >>> 0;
      const t1 = (h + S1 + ch + K[i] + w[i]) >>> 0;
      const S0 = (rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) >>> 0;
      const maj = ((a & b) ^ (a & c) ^ (b & c)) >>> 0;
      const t2 = (S0 + maj) >>> 0;
      h = g; g = f; f = e; e = (d + t1) >>> 0;
      d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }

    h0 = (h0 + a) >>> 0; h1 = (h1 + b) >>> 0; h2 = (h2 + c) >>> 0; h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0; h5 = (h5 + f) >>> 0; h6 = (h6 + g) >>> 0; h7 = (h7 + h) >>> 0;
  }

  return [h0, h1, h2, h3, h4, h5, h6, h7]
    .map(x => x.toString(16).padStart(8, '0'))
    .join('');
}

// --- document content hashes ------------------------------------------------

// The hash for a hosted-form / acknowledge document: its form_schema, exactly
// as stored (NOT coerced — coercion defaults could drift between releases and
// silently change the hash of content nobody edited).
export function hashSchema(schema) {
  return sha256Hex(canonicalJsonString(schema ?? null));
}

// The hash for a pdf_sign document: the SOURCE PDF BYTES (brief §7 — the source
// PDF is stored unaltered and the hash is taken over it), plus the field layout,
// which is part of what the supplier is presented with. Caller fetches the bytes
// from storage; this stays pure.
export function hashPdfSource(pdfBytes, fieldLayout) {
  return sha256Hex(`${sha256Hex(pdfBytes)}:${canonicalJsonString(fieldLayout ?? [])}`);
}

// The one hashing rule, expressed once. Both shapes of the same document —
// the fusion_documents row (snake_case, admin side) and the portal's docMeta
// (camelCase, supplier side) — normalise into this, so a document cannot hash
// one way when it's published and another way when it's signed.
//
// What gets hashed is whatever the supplier is actually shown:
//   pdf_sign    → the source PDF bytes + the field layout placed on them
//   form        → the form_schema
//   acknowledge → the rich text (or the linked file, when the content IS a file)
function contentHashOfContent(content, { pdfBytes } = {}) {
  const { satisfiedBy, formSchema, contentRichtext, contentFileUrl, fieldLayout } = content;
  if (satisfiedBy === 'pdf_sign') {
    // A hash of a storage PATH would look like evidence while proving nothing —
    // the bytes at that path can change. No bytes, no hash.
    return pdfBytes ? hashPdfSource(pdfBytes, fieldLayout) : null;
  }
  if (formSchema != null) return hashSchema(formSchema);
  if (contentRichtext || contentFileUrl) {
    return sha256Hex(canonicalJsonString({ richtext: contentRichtext || null, file: contentFileUrl || null }));
  }
  return null;
}

// Hash a fusion_documents row (admin side — publish).
export function contentHashForDocument(doc, options = {}) {
  if (!doc) return null;
  return contentHashOfContent({
    satisfiedBy: doc.satisfied_by,
    formSchema: doc.form_schema,
    contentRichtext: doc.content_richtext,
    contentFileUrl: doc.content_file_url,
    fieldLayout: doc.field_layout,
  }, options);
}

// Hash the portal's document meta (supplier side — signing). Same rule, same
// output: the hash taken at signing is directly comparable with the one stored
// at publish, which is the whole point of the check.
export function contentHashForMeta(meta, options = {}) {
  if (!meta) return null;
  return contentHashOfContent({
    satisfiedBy: meta.satisfiedBy,
    formSchema: meta.formSchema,
    contentRichtext: meta.contentRichtext,
    contentFileUrl: meta.contentFileUrl,
    fieldLayout: meta.fieldLayout,
  }, options);
}

// Is the document about to be signed the one that was published? Returns the
// verify shape ('match' | 'mismatch' | 'unknown'). A mismatch means the version
// row changed after publish — the supplier must NOT be asked to sign it
// (versioning §3: surface loudly, never render on regardless).
export function verifyMetaBeforeSigning(meta, options = {}) {
  const stored = meta?.contentHash || null;
  if (!stored) return { state: 'unknown', stored: null, actual: null };
  const actual = contentHashForMeta(meta, options);
  if (!actual) return { state: 'unknown', stored, actual: null };
  return actual === stored
    ? { state: 'match', stored, actual }
    : { state: 'mismatch', stored, actual };
}

// --- verification -----------------------------------------------------------

// Re-derive the hash and compare with the one stored at publish. A mismatch is
// evidence of tampering or a bug and must surface LOUDLY (brief §3) — never be
// swallowed into "render it anyway".
//
// Returns one of:
//   { state: 'unknown' }   — no stored hash (published before hashing existed,
//                            or a pdf_sign doc whose bytes weren't supplied).
//                            Not a pass and not a failure — say "unknown".
//   { state: 'match' }
//   { state: 'mismatch', stored, actual }
export function verifyContentHash(doc, options = {}) {
  const stored = doc?.content_hash || null;
  if (!stored) return { state: 'unknown', stored: null, actual: null };
  const actual = contentHashForDocument(doc, options);
  if (!actual) return { state: 'unknown', stored, actual: null };
  return actual === stored
    ? { state: 'match', stored, actual }
    : { state: 'mismatch', stored, actual };
}

// --- publish choices (brief A §1) -------------------------------------------

// What a publish does to suppliers who signed the PREVIOUS version. Stored on
// the version row (resign_policy), so a version always carries its own answer.
export const RESIGN_POLICIES = {
  NONE: 'none',           // A — nothing changes; existing signatures stay valid
  DEADLINE: 'deadline',   // B — sign again by a date; compliant until then
  IMMEDIATE: 'immediate', // C — non-compliant now; regulation-forced only
};

// What is a signature against a given version still worth TODAY?
//
// The answer isn't on the signature — it's decided by every version published
// AFTER it (brief A §1): a publish that said "nothing changes" leaves earlier
// signatures valid; one that demanded re-signing does not. Under a deadline
// (B) the supplier stays compliant right up to the date and only then lapses,
// which is why "still valid" and "historic" can be the same number meaning
// different things during a re-sign period (brief B §3).
//
// Returns { state, dueDate, reason }:
//   'valid'       — nothing since required re-signing
//   'resign_due'  — re-signing required by dueDate, still compliant until then
//   'lapsed'      — that deadline has passed; not compliant
//   'invalid_now' — a version demanded immediate re-signing (C)
export function signatureStandingFor(version, allVersions, options = {}) {
  const today = options.todayIso || new Date().toISOString().slice(0, 10);
  const versionNumber = Number(version?.version_value) || 0;

  // Only PUBLISHED versions can demand anything of a supplier; a draft is not
  // out there yet.
  const requiring = (allVersions || [])
    .filter(v => v && v.status !== 'draft')
    .filter(v => (Number(v.version_value) || 0) > versionNumber)
    .filter(v => v.resign_policy && v.resign_policy !== RESIGN_POLICIES.NONE)
    .sort((a, b) => (Number(b.version_value) || 0) - (Number(a.version_value) || 0));

  if (requiring.length === 0) return { state: 'valid', dueDate: null, reason: null };

  // The most recent demand governs — it supersedes any earlier one.
  const governing = requiring[0];
  const reason = governing.change_reason || null;

  if (governing.resign_policy === RESIGN_POLICIES.IMMEDIATE) {
    return { state: 'invalid_now', dueDate: null, reason };
  }
  const dueDate = governing.resign_deadline || null;
  if (!dueDate) return { state: 'resign_due', dueDate: null, reason };
  return dueDate >= today
    ? { state: 'resign_due', dueDate, reason }
    : { state: 'lapsed', dueDate, reason };
}

// A signature still counts towards compliance while it is valid OR inside its
// re-sign window. Lapsed and immediate do not.
export function standingCountsAsCompliant(state) {
  return state === 'valid' || state === 'resign_due';
}

// Fold a re-sign requirement into the supplier's compliance record.
//
// Deliberately expressed as a PATCH to the existing record shape rather than a
// new document state: "you signed, but a newer version needs signing by the
// 30th" is the same shape as "approved, expires on the 30th", and "that date
// passed" is the same shape as "not provided". Reusing them means the counts,
// colours, blocking rule, stripe copy and admin queue all behave correctly
// without a new state threaded through every one of them.
//
// Returns null when nothing is owed, otherwise:
//   { drop: true }              — the signature no longer counts; the document
//                                 returns to Outstanding (they must sign again)
//   { expiry_date, resign }     — still compliant, but with a deadline
export function resignRecordPatch(standing, record) {
  if (!standing || standing.state === 'valid') return null;

  if (standing.state === 'resign_due') {
    const existing = record?.expiry_date || null;
    // Whichever comes first governs — a re-sign deadline must never push a
    // genuine document expiry later.
    const expiry = existing && standing.dueDate
      ? (existing < standing.dueDate ? existing : standing.dueDate)
      : (standing.dueDate || existing);
    return { expiry_date: expiry, resign: standing };
  }

  // 'lapsed' or 'invalid_now' — not compliant, and blocked from new work.
  return { drop: true, resign: standing };
}

// Mirrors the DB CHECK constraint (fusion_documents_resign_fields_check) so the
// UI can block a bad publish before the round-trip, with the same rules.
export function publishChoiceProblems({ resignPolicy, changeReason, resignDeadline }, options = {}) {
  const problems = [];
  const policy = resignPolicy || RESIGN_POLICIES.NONE;
  if (!Object.values(RESIGN_POLICIES).includes(policy)) {
    problems.push('Choose what happens to suppliers who signed the previous version.');
    return problems;
  }
  if (policy === RESIGN_POLICIES.NONE) return problems;

  if (!String(changeReason || '').trim()) {
    problems.push('Give a reason for the change — the supplier sees it when they re-sign.');
  }
  if (policy === RESIGN_POLICIES.DEADLINE) {
    if (!resignDeadline) {
      problems.push('Set the deadline for signing again.');
    } else if (options.todayIso && resignDeadline <= options.todayIso) {
      problems.push('The deadline must be in the future.');
    }
  }
  return problems;
}
